import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import 'react-loading-skeleton/dist/skeleton.css';
import {User} from '../../../entity/model/user';
import {UserInvitationDto} from '../../../entity/dto/user-invitation-dto';
import {useService} from '../../../infrastructure/service/service';
import {UserPermission} from '../../../entity/enums/user-permission';
import {Organization} from '../../../entity/model/organization';
import {ManageMode} from '../../../entity/enums/manage-mode';
import {InvitationStatus} from '../../../entity/enums/invitation-status';
import {toast} from 'react-toastify';
import {useNavigate} from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import {
  Button, Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Table, TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import TableSortLabel from '@mui/material/TableSortLabel';
import LoadingComponent from '../components/widget/LoadingComponent';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DeleteIcon from '@mui/icons-material/Delete';
import CachedIcon from '@mui/icons-material/Cached';
import moment from 'moment/moment';
import ConfirmDialog from '../components/widget/ConfirmDialog';
import UserInvitationDialog from '../components/users/UserInvitationDialog';
import TablePagination from '../components/widget/TablePagination';
import RefreshIcon from '@mui/icons-material/Refresh';
import {Form, Formik, FormikProps} from 'formik';
import {SearchInputDto} from '../../../entity/dto/search-input-dto';

import '../../../assets/css/style.css';

const UserInvitations = (): JSX.Element => {
  const {userService, organizationService} = useService();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [createUserPermission, setCreateUserPermission] = useState<boolean>(false);
  const [updateUserPermission, setUpdateUserPermission] = useState<boolean>(false);
  const [deleteUserPermission, setDeleteUserPermission] = useState<boolean>(false);
  const [organization, setOrganization] = useState<Organization>();
  const [openUserDialog, setOpenUserDialog] = useState<boolean>(false);
  const [selectedInvitation, setSelectedInvitation] = useState<UserInvitationDto>();
  const [invitations, setInvitations] = useState<UserInvitationDto[]>([]);
  const [searchInvitations, setSearchInvitations] = useState<UserInvitationDto[]>([]);
  const [currentUser, setCurrentUser] = useState<User>();
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [menuElement, setMenuElement] = useState<null | HTMLElement>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSearchInput, setCurrentSearchInput] = useState<string>('');

  const pageSize = 100;
  const tableHeaders = ['Email', 'Roles', 'Status', 'Days Remaining', 'Expires At', 'Created At', 'Invitation Code'];

  useEffect(() => {
    const init = async (): Promise<void> => {
      setLoading(true);

      const user = await userService.getCurrentUser();
      setCurrentUser(user);

      setCreateUserPermission(userService.isPermitted(UserPermission.CREATE_ORG_USER));
      setUpdateUserPermission(userService.isPermitted(UserPermission.UPDATE_ORG_USER));
      setDeleteUserPermission(userService.isPermitted(UserPermission.DELETE_ORG_USER));

      setOrganization(await organizationService.getOrganizationById(user?.organizationID ?? ''));

      const invitations = await userService.getUserInvitations(user?.organizationID ?? '', false);
      setInvitations(invitations);
      setSearchInvitations(invitations.slice(0, pageSize));
      setTotalPages(Math.ceil(invitations.length / pageSize));
    }

    init().then(() => setLoading(false));
  }, [userService.getCache()])

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, invitation: UserInvitationDto): void => {
    setMenuElement(event.currentTarget);
    setSelectedInvitation(invitation);
  }

  const handleMenuClose = (): void => {
    setMenuElement(null);
  }

  const handleSearchChange = (handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void) => (event: React.ChangeEvent<HTMLInputElement>): void => {
    handleChange(event);

    const input = event.target.value.trim();
    setCurrentSearchInput(input);

    const inputLower = input.toLowerCase();
    if (!inputLower) {
      setCurrentPage(1);
      setTotalPages(Math.ceil(invitations.length / pageSize));
      setSearchInvitations(invitations.slice(0, pageSize));
      return;
    }

    const filteredInvitations = userService.filterUserInvitations(inputLower);
    setCurrentPage(1);
    setTotalPages(Math.ceil(filteredInvitations.length / pageSize));
    setSearchInvitations(filteredInvitations.slice(0, pageSize));
  };

  const handlePaginationChange = (event: React.ChangeEvent<unknown>, activePage: number): void => {
    setCurrentPage(activePage);

    const startIndex = (activePage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setSearchInvitations(invitations.slice(startIndex, endIndex));
  }

  const handleOpenUserDialog = (mode: ManageMode): void => {
    if (mode !== ManageMode.CREATE)
      handleMenuClose();

    setOpenUserDialog(true);
  }

  const handleDeleteInvitation = (): void => {
    handleMenuClose();
    setOpenConfirmDialog(true);
  }

  const deleteInvitation = async (): Promise<void> => {
    setOpenConfirmDialog(false);
    setLoading(true);

    if (selectedInvitation?.id)
      await userService.deleteInvitation(organization?.id ?? '', selectedInvitation.id);

    await refreshPage();
    setLoading(false);
  }

  const handleResendInvitation = async (): Promise<void> => {
    handleMenuClose();
    setLoading(true);

    if (selectedInvitation?.id)
      await userService.resendInvitation(organization?.id ?? '', selectedInvitation.id);

    await refreshPage();
    setLoading(false);
  }

  const refreshPage = async (): Promise<void> => {
    setRefresh(true);

    const invitations = await userService.getUserInvitations(currentUser?.organizationID ?? '', true);
    setInvitations(invitations);

    if (currentSearchInput && currentSearchInput.trim()) {
      const filteredInvitations = userService.filterUserInvitations(currentSearchInput.toLowerCase());
      setSearchInvitations(filteredInvitations.slice(0, pageSize));
      setTotalPages(Math.ceil(filteredInvitations.length / pageSize));
    } else {
      setSearchInvitations(invitations.slice(0, pageSize));
      setTotalPages(Math.ceil(invitations.length / pageSize));
    }

    setCurrentPage(1);
    setRefresh(false);
  }

  const renderTableRow = (invitation: UserInvitationDto, index: number): JSX.Element => {
    return (
      <TableRow
        hover
        tabIndex={-1}
        key={invitation.id}
        className={'table-cell'}>
        <TableCell className={'no-wrap'} padding='checkbox'>
          <IconButton
            id={`btn-invitation-menu-${index}`}
            className={'zitles-menu-trigger'}
            color= "inherit"
            disableRipple
            disableFocusRipple
            onClick={(event: React.MouseEvent<HTMLElement>): void => handleMenuOpen(event, invitation)}>
            <MoreHorizIcon />
          </IconButton>
          <Menu elevation={0} anchorEl={menuElement} open={Boolean(menuElement)} className={'zitles-menu'} onClose={handleMenuClose}>
            <MenuItem
              id={`menu-invitation-resend-${index}`}
              disableRipple
              className={'zitles-menu-item'}
              disabled={loading || selectedInvitation?.status === InvitationStatus.ACCEPTED}
              onClick={handleResendInvitation}>
              <CachedIcon className={'zitles-icon'}/>
              <Typography className={'zitles-menu-item button'}>{loading ? 'Processing...' : 'Resend'}</Typography>
            </MenuItem>
            <Divider/>
            <MenuItem
              id={`menu-invitation-delete-${index}`}
              disableRipple
              className={'zitles-menu-item'}
              disabled={loading || selectedInvitation?.status === InvitationStatus.ACCEPTED || selectedInvitation?.status === InvitationStatus.REVOKED}
              onClick={handleDeleteInvitation}>
              <DeleteIcon className={'zitles-icon'}/>
              <Typography className={'zitles-menu-item button'}>{loading ? 'Processing...' : 'Revoke'}</Typography>
            </MenuItem>
          </Menu>
        </TableCell>
        <TableCell id={`text-invitation-email-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{invitation.email}</TableCell>
        <TableCell id={`text-invitation-roles-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{invitation.roles}</TableCell>
        <TableCell id={`text-invitation-status-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{invitation.status}</TableCell>
        <TableCell id={`text-invitation-days-remaining-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{invitation.daysRemaining}</TableCell>
        <TableCell id={`text-invitation-expires-at-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{moment.utc(invitation.expiresAt).format('YYYY-MM-DD')}</TableCell>
        <TableCell id={`text-invitation-created-at-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{moment.utc(invitation.createdAt).format('YYYY-MM-DD')}</TableCell>
        <TableCell id={`text-invitation-code-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{invitation.invitationCode}</TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <Box className={'body-box'}>
        {
          refresh &&
            <LoadingComponent/>
        }
        <Paper className={'table-container'} elevation={0}>
              <Box className={'toolbar-container'}>
                {
                  createUserPermission && organization?.isPublic === true &&
                    <Button
                      id={'btn-user-create'}
                      className={'zitles-button'}
                      variant="contained"
                      startIcon={<AddIcon/>}
                      disabled={!createUserPermission}
                      onClick={(): void => handleOpenUserDialog(ManageMode.CREATE)}>
                      Invite User
                    </Button>
                }
                {
                  createUserPermission &&
                    <Button
                      id={'btn-users'}
                      className={'zitles-button users-button'}
                      variant="outlined"
                      onClick={(): void => navigate('/users')}>
                      Users
                    </Button>
                }
                <Formik initialValues={userService.getSearchDto()} onSubmit={(): void => {}}>
                  {({values, handleChange}: FormikProps<SearchInputDto>): JSX.Element => (
                    <Form>
                      <TextField
                        id={'search-user'}
                        name='searchInput'
                        className={createUserPermission ? 'zitles-searchbar' : 'zitles-searchbar no-padding'}
                        variant={'outlined'}
                        placeholder="Search"
                        value={values.searchInput}
                        onChange={handleSearchChange(handleChange)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon className={'zitles-searchbar icon'}/>
                            </InputAdornment>)
                        }}/>
                    </Form>
                  )}
                </Formik>
                <Button
                  id={'btn-user-refresh'}
                  className={'zitles-button zitles-table refresh'}
                  variant='contained'
                  startIcon={<RefreshIcon />}
                  disabled={refresh}
                  onClick={refreshPage}>
                  Refresh
                </Button>
              </Box>
              {
                searchInvitations.length > 0
                  ? <TableContainer className={'zitles-table'} component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow className={'table-header'}>
                            <TableCell className={'no-wrap'} padding="checkbox"/>
                            {
                              tableHeaders.map((header: string): JSX.Element => (
                                <TableCell key={header} className={'no-wrap'} align='left' padding='normal'>
                                  <TableSortLabel
                                    id={`invitation-column-${header}`}
                                    className={'table-header-text'}
                                    active={true}
                                    direction='desc'>
                                    {header}
                                  </TableSortLabel>
                                </TableCell>
                              ))
                            }
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {
                            searchInvitations.map(renderTableRow)
                          }
                        </TableBody>
                      </Table>
                    </TableContainer>
                  : <Typography id={'text-invitation-empty'} className='empty-message'>No invitations found !!</Typography>
              }
            </Paper>
            {
              searchInvitations.length > 0 &&
                <TablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onChange={handlePaginationChange}/>
            }
          </Box>
      {
        loading &&
          <LoadingComponent/>
      }
      {
        openUserDialog &&
          <UserInvitationDialog
            open={openUserDialog}
            setOpen={setOpenUserDialog}/>
      }
      {
        openConfirmDialog &&
          <ConfirmDialog
            open={openConfirmDialog}
            setOpen={setOpenConfirmDialog}
            title={'Revoke Invitation'}
            message={'Are you sure you want to revoke this invitation?'}
            handleSubmit={deleteInvitation}
            loading={loading}/>
      }
    </>
  )
}

export default observer(UserInvitations);
